// App.js file added a comment here

import React, { useState, useRef, useEffect } from 'react';
import { Copy, Printer, Volume2, Send, Save, List, Eye, Trash2, Check, ChevronRight, ChevronDown } from 'lucide-react';

const FrogIcon = ({ size = 24 }) => (
  <img 
    src="/logo_192.png" 
    alt="Trilingue Logo" 
    style={{ width: `${size}px`, height: `${size}px` }}
  />
);

const SnailIcon = ({ size = 24 }) => (
  <svg
    width={size}
    height={size * 0.75}
    viewBox="0 0 32 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4015"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.5238,5.8114C14.1955.0333,6.699-.0501,3.0988,4.5672.2187,8.261.8047,12.7003,4.4986,15.5804c2.9551,2.3041,7.2184,1.7763,9.5225-1.1787,1.8433-2.3641.6388-6.0057-1.584-7.7388-1.7783-1.3865-4.3439-1.069-5.7304.7093-1.1092,1.4226-.8552,3.4751.5675,4.5843,1.1381.8874,2.7314.5035,3.1631-.2674.2277-.4067-.1382-1.0834-.4468-.8308"/>
    <path d="M30.7557,14.0478c0,.0322-.0008.5158-.0008.5478,0,4.3323-2.3023,8.1852-8.4321,8.1852H6.2473c0-1.9262,3.6815-.8081,7.2714-2.4709,5.7128-2.6462,5.6661-7.1706,5.6661-7.5203,0-2.7449,2.6906-4.7839,5.5288-4.7839,3.337,0,6.0421,2.7051,6.0421,6.0421Z"/>
    <path d="M20.1447,3.002c-.0248.9771.7472,1.7892,1.7243,1.814s1.7892-.7472,1.814-1.7243c.0248-.9771-.6495-1.7867-1.7243-1.814s-1.7892.7472-1.814,1.7243Z"/>
    <path d="M22.6763,4.9683c.3537.7902.7074,1.5803,1.0612,2.3705"/>
    <path d="M30.7708,3.0482c-.0019.9774-.7958,1.7682-1.7732,1.7662s-1.7682-.7958-1.7662-1.7732.698-1.7683,1.7732-1.7662,1.7682.7958,1.7662,1.7732Z"/>
    <path d="M28.1865,4.9448c-.3751.7802-.7503,1.5604-1.1254,2.3407"/>
  </svg>
);

const TrilingualTranslator = () => {
  const [messages, setMessages] = useState([]);
  const [currentView, setCurrentView] = useState('translate');
  const [savedConversations, setSavedConversations] = useState([]);
  const [expandedLines, setExpandedLines] = useState({});

  const [inputs, setInputs] = useState({
    english: '',
    french: '',
    spanish: ''
  });
  const [isRecording, setIsRecording] = useState(null);
  const [playingMessageId, setPlayingMessageId] = useState(null);
  const [activeLanguage, setActiveLanguage] = useState('english');
  const [availableLanguages] = useState({
    english: 'English',
    french: 'Français', 
    spanish: 'Español',
    japanese: '日本語',
    german: 'Deutsch',
    italian: 'Italiano',
    portuguese: 'Português',
    mandarin: '中文',
    polish: 'Polski'  // Add this line
  });
  const [selectedLanguages, setSelectedLanguages] = useState(['english', 'french', 'spanish']);
  
  // Usage tracking state
  const [usage, setUsage] = useState({
    ocr: { percentage: 0, cost: 0 },
    tts: { percentage: 0, cost: 0 },
    stt: { percentage: 0, cost: 0 },
    translation: { percentage: 0, cost: 0 }
  });
  
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    setInputs(prev => {
      const newInputs = { ...prev };
      selectedLanguages.forEach(lang => {
        if (!(lang in newInputs)) {
          newInputs[lang] = '';
        }
      });
      return newInputs;
    });
  }, [selectedLanguages]);

  const playRibbitSound = () => {
  const audio = new Audio('/frog-sound.mp3');
  audio.volume = 0.3; // Adjust volume (0.0 to 1.0)
  audio.play().catch(error => console.log('Audio play failed:', error));
};

  // Real Google Translate API call
  const translateText = async (text, targetLang, sourceLang) => {
    try {
      const response = await fetch('/api/translate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: text,
          targetLanguage: targetLang,
          sourceLanguage: sourceLang
        })
      });

      const result = await response.json();
      
      if (result.error) {
        console.error('Translation error:', result.error);
        return `[Translation failed: ${result.error}]`;
      }

      // Update usage tracking
      if (result.usage) {
        setUsage(prev => ({
          ...prev,
          translation: {
            percentage: Math.min(prev.translation.percentage + 1, 100),
            cost: prev.translation.cost + (result.usage.characters * 0.00002) // Approximate Google pricing
          }
        }));
      }

      return result.translatedText;
    } catch (error) {
      console.error('Translation fetch error:', error);
      return `[Translation error: ${error.message}]`;
    }
  };

  const handleSendMessage = async (inputLanguage) => {
    const messageText = inputs[inputLanguage] || '';
    if (!messageText.trim()) return;

    // Get the other two languages for translation
    const targetLanguages = selectedLanguages.filter(lang => lang !== inputLanguage);

    // Create input message (gray bubble on left)
    const inputMessage = {
      id: Date.now(),
      text: messageText,
      language: inputLanguage,
      type: 'input',
      isSaved: false
    };

    // Add input message immediately
    setMessages(prev => [...prev, inputMessage]);

    try {
      // Create translation messages (blue bubbles on right)
      const translation1 = await translateText(messageText, targetLanguages[0], inputLanguage);
      const translation2 = await translateText(messageText, targetLanguages[1], inputLanguage);

      const translationMessage1 = {
        id: Date.now() + 1,
        text: translation1,
        language: targetLanguages[0],
        type: 'translation',
        isSaved: false
      };

      const translationMessage2 = {
        id: Date.now() + 2,
        text: translation2,
        language: targetLanguages[1],
        type: 'translation',
        isSaved: false
      };

      // Add translation messages
      setMessages(prev => [...prev, translationMessage1, translationMessage2]);
    } catch (error) {
      console.error('Error in translation:', error);
    }
    
    // Clear input
    setInputs(prev => ({ ...prev, [inputLanguage]: '' }));
    setActiveLanguage(inputLanguage);
  };

  const handleLanguageChange = (index, language) => {
    playRibbitSound();
    setSelectedLanguages(prev => {
      const newLanguages = [...prev];
      newLanguages[index] = language;
      return newLanguages;
    });
  };

  // Real Google Text-to-Speech API call
  const handlePlayMessage = async (messageId, text, language, speed = 'normal') => {
    const playbackKey = `${messageId}-${speed}`;
    
    if (playingMessageId === playbackKey) {
      setPlayingMessageId(null);
      return;
    }

    setPlayingMessageId(playbackKey);

    try {
      const response = await fetch('/api/text-to-speech', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: text,
          language: language
        })
      });

      const result = await response.json();

      if (result.error) {
        console.error('TTS error:', result.error);
        setPlayingMessageId(null);
        return;
      }

      // Update usage tracking
      if (result.usage) {
        setUsage(prev => ({
          ...prev,
          tts: {
            percentage: Math.min(prev.tts.percentage + 1, 100),
            cost: prev.tts.cost + (result.usage.characters * 0.000016)
          }
        }));
      }

      // Play the audio
      if (result.audioContent) {
        const audioBlob = new Blob([Uint8Array.from(atob(result.audioContent), c => c.charCodeAt(0))], {type: 'audio/mpeg'});
        const audio = new Audio(URL.createObjectURL(audioBlob));
        
        // Set playback rate based on speed parameter
        const playbackRate = speed === 'slow' ? 0.5 : 0.82;
        
        audio.onloadedmetadata = () => {
          audio.playbackRate = playbackRate;
          console.log(`Playback rate set to: ${playbackRate} (${speed} speed)`);
        };
        
        audio.onended = () => setPlayingMessageId(null);
        audio.onerror = () => setPlayingMessageId(null);
        
        audio.addEventListener('canplay', () => {
          audio.playbackRate = playbackRate;
        });
        
        await audio.play();
      } else {
        setPlayingMessageId(null);
      }
    } catch (error) {
      console.error('TTS fetch error:', error);
      setPlayingMessageId(null);
    }
  };

  const handleCopyMessage = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Real Google Speech-to-Text API call
  const startRecording = async (language) => {
    // If already recording this language, stop it
    if (isRecording === language) {
      // Stop the recording
      if (window.currentMediaRecorder && window.currentMediaRecorder.state === 'recording') {
        window.currentMediaRecorder.stop();
      }
      setIsRecording(null);
      return;
    }

    setIsRecording(language);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const chunks = [];

      // Store recorder globally so we can stop it on button press
      window.currentMediaRecorder = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        chunks.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: 'audio/webm' });
        const reader = new FileReader();
        
        reader.onloadend = async () => {
          const base64Audio = reader.result.split(',')[1];
          
          try {
            const response = await fetch('/api/speech-to-text', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                audioContent: base64Audio,
                language: language
              })
            });

            const result = await response.json();

            if (result.error) {
              console.error('STT error:', result.error);
              setIsRecording(null);
              return;
            }

            // Update usage tracking
            if (result.usage) {
              setUsage(prev => ({
                ...prev,
                stt: {
                  percentage: Math.min(prev.stt.percentage + 1, 100),
                  cost: prev.stt.cost + (result.usage.audioLength * 0.006)
                }
              }));
            }

            // Set the transcribed text in the input field
            if (result.transcription) {
              setInputs(prev => ({ ...prev, [language]: result.transcription }));
            }
          } catch (error) {
            console.error('STT fetch error:', error);
          }
          
          setIsRecording(null);
          window.currentMediaRecorder = null;
        };
        
        reader.readAsDataURL(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      
      // Auto-stop after 10 seconds as a failsafe
      setTimeout(() => {
        if (window.currentMediaRecorder && window.currentMediaRecorder.state === 'recording') {
          window.currentMediaRecorder.stop();
        }
      }, 10000);

    } catch (error) {
      console.error('Recording error:', error);
      setIsRecording(null);
      window.currentMediaRecorder = null;
    }
  };

  // OCR functionality for + button
  const handleImageUpload = async (language) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result.split(',')[1];
        
        try {
          const response = await fetch('/api/ocr', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              imageContent: base64Image,
              language: language
            })
          });

          const result = await response.json();

          if (result.error) {
            console.error('OCR error:', result.error);
            return;
          }

          // Update usage tracking
          if (result.usage) {
            setUsage(prev => ({
              ...prev,
              ocr: {
                percentage: Math.min(prev.ocr.percentage + 1, 100),
                cost: prev.ocr.cost + (result.usage.imageSize * 0.0015) // Approximate Google pricing
              }
            }));
          }

          // Set the extracted text in the input field
          if (result.extractedText) {
            setInputs(prev => ({ ...prev, [language]: result.extractedText }));
          }
        } catch (error) {
          console.error('OCR fetch error:', error);
        }
      };
      
      reader.readAsDataURL(file);
    };
    
    input.click();
  };

  const exportChat = () => {
    const chatText = messages.map(msg => 
      `${msg.type === 'input' ? 'Input' : 'Translation'} (${availableLanguages[msg.language]}): ${msg.text}`
    ).join('\n\n');
    navigator.clipboard.writeText(chatText);
  };

  const printChat = () => {
    const chatContent = messages.map(msg =>
      `${msg.type === 'input' ? 'Input' : 'Translation'} (${availableLanguages[msg.language]}): ${msg.text}`
    ).join('\n\n');

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head><title>Translation Export</title></head>
        <body style="font-family: Arial, sans-serif; padding: 20px;">
          <h2>Trilingue Translation Export</h2>
          <pre style="white-space: pre-wrap;">${chatContent}</pre>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const saveChat = () => {
    if (messages.length === 0) return;

    // Group messages into translation sets (1 input + 2 translations)
    const newConversations = [];
    const messageIdsToMarkSaved = [];

    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];

      if (msg.type === 'input' && !msg.isSaved) {
        // Find the next two translation messages
        const translation1 = messages[i + 1];
        const translation2 = messages[i + 2];

        if (translation1 && translation2 && translation1.type === 'translation' && translation2.type === 'translation') {
          newConversations.push({
            timestamp: new Date().toISOString(),
            originalText: msg.text,
            originalLanguage: msg.language,
            translation1Text: translation1.text,
            translation1Language: translation1.language,
            translation2Text: translation2.text,
            translation2Language: translation2.language
          });

          // Track these message IDs to mark as saved
          messageIdsToMarkSaved.push(msg.id, translation1.id, translation2.id);

          // Skip the translation messages we just processed
          i += 2;
        }
      }
    }

    if (newConversations.length === 0) {
      console.log('No new conversations to save');
      return;
    }

    // Get existing saved conversations from localStorage
    const existingSaved = JSON.parse(localStorage.getItem('trilingueConversations') || '[]');

    // Add new conversations
    const updatedSaved = [...existingSaved, ...newConversations];

    // Save to localStorage
    localStorage.setItem('trilingueConversations', JSON.stringify(updatedSaved));

    // Mark messages as saved
    setMessages(prevMessages =>
      prevMessages.map(msg =>
        messageIdsToMarkSaved.includes(msg.id)
          ? { ...msg, isSaved: true }
          : msg
      )
    );

    console.log(`Saved ${newConversations.length} new conversation(s) to localStorage`);
  };

  const toggleView = () => {
    const newView = currentView === 'translate' ? 'saved' : 'translate';
    setCurrentView(newView);

    // Load saved conversations when switching to saved view
    if (newView === 'saved') {
      const saved = JSON.parse(localStorage.getItem('trilingueConversations') || '[]');
      setSavedConversations(saved);
    }

    console.log(`Current view: ${newView}`);
  };

  const truncateText = (text, maxLength = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}h${minutes}`;
  };

  const toggleLineExpansion = (conversationIndex, lineType) => {
    const key = `${conversationIndex}-${lineType}`;
    setExpandedLines(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const loadConversationToView = (conversation, scrollToType = 'input') => {
    // Create message objects for the conversation
    const inputMessage = {
      id: Date.now(),
      text: conversation.originalText,
      language: conversation.originalLanguage,
      type: 'input',
      isSaved: true
    };

    const translation1Message = {
      id: Date.now() + 1,
      text: conversation.translation1Text,
      language: conversation.translation1Language,
      type: 'translation',
      isSaved: true
    };

    const translation2Message = {
      id: Date.now() + 2,
      text: conversation.translation2Text,
      language: conversation.translation2Language,
      type: 'translation',
      isSaved: true
    };

    // Set the messages
    setMessages([inputMessage, translation1Message, translation2Message]);

    // Switch to translate view
    setCurrentView('translate');

    // Scroll to the appropriate message after a brief delay to ensure DOM is updated
    setTimeout(() => {
      let targetElement = null;

      if (scrollToType === 'input') {
        // Scroll to the input (gray) bubble - look for bg-gray-700 class
        const grayBubbles = document.querySelectorAll('.bg-gray-700');
        if (grayBubbles.length > 0) {
          targetElement = grayBubbles[0];
        }
      } else if (scrollToType === 'translation1') {
        // Scroll to first blue translation bubble
        const blueBubbles = document.querySelectorAll('.bg-blue-600');
        if (blueBubbles.length > 0) {
          targetElement = blueBubbles[0];
        }
      } else if (scrollToType === 'translation2') {
        // Scroll to second blue translation bubble
        const blueBubbles = document.querySelectorAll('.bg-blue-600');
        if (blueBubbles.length > 1) {
          targetElement = blueBubbles[1];
        }
      }

      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const deleteConversation = (indexToDelete) => {
    // Get current saved conversations
    const saved = JSON.parse(localStorage.getItem('trilingueConversations') || '[]');

    // Remove the item at the specified index
    const updated = saved.filter((_, index) => index !== indexToDelete);

    // Save back to localStorage
    localStorage.setItem('trilingueConversations', JSON.stringify(updated));

    // Update state to refresh the view
    setSavedConversations(updated);
  };

  const getPlaceholderText = (language) => {
    const placeholders = {
      english: "Type in English...",
      french: "Tapez en français...",
      spanish: "Escribe en español...",
      japanese: "日本語で入力...",
      german: "Auf Deutsch schreiben...",
      italian: "Scrivi in italiano...",
      portuguese: "Digite em português...",
      mandarin: "用中文输入...",
      polish: "Wpisz po polsku..."  // Add this line
    };
    return placeholders[language] || `Type in ${availableLanguages[language]}...`;
  };

  const formatUsageDisplay = (apiData) => {
    return apiData.percentage.toString();
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse-blue-white {
            0%, 100% {
              stroke: #ffffff;
            }
            50% {
              stroke: #3b82f6;
            }
          }
          .pulse-mic {
            animation: pulse-blue-white 1s ease-in-out infinite;
          }
        `}
      </style>
      <div className="flex flex-col h-screen bg-gray-900 max-w-md mx-auto">
        {/* Header */}
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FrogIcon size={96} />
            <h1 className="text-xl font-medium text-white">Trilingue</h1>
          </div>
          <div className="flex flex-col items-end space-y-2">
            {/* First line: List */}
            <div className="flex">
              <button onClick={toggleView} className="text-gray-400 hover:text-gray-200">
                <List size={24} />
              </button>
            </div>
            {/* Second line: Print-Copy-Save */}
            <div className="flex space-x-3">
              <button onClick={printChat} className="text-gray-400 hover:text-gray-200">
                <Printer size={24} />
              </button>
              <button onClick={exportChat} className="text-gray-400 hover:text-gray-200">
                <Copy size={24} />
              </button>
              <button onClick={saveChat} className="text-gray-400 hover:text-gray-200">
                <Save size={24} />
              </button>
            </div>
          </div>
        </div>

        {/* Messages or Saved View */}
        {currentView === 'translate' ? (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            {messages.map((message) => (
              <div key={message.id} className={`flex ${message.type === 'input' ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-sm relative group ${
                  message.type === 'input'
                    ? 'bg-gray-700 text-gray-100 rounded-2xl rounded-bl-md'
                    : 'bg-blue-600 text-white rounded-2xl rounded-br-md'
                }`}>
                {/* Button row at the top */}
                <div className="flex justify-between items-center px-4 pt-3 pb-1">
                  {/* Left side: Checkmark for saved messages */}
                  <div className="flex-shrink-0">
                    {message.isSaved ? (
                      <Check size={20} className="text-white" />
                    ) : (
                      <div style={{ width: '20px' }} />
                    )}
                  </div>

                  {/* Right side: Action buttons */}
                  <div className="flex space-x-2">
                    <button
                      className={`p-2 rounded-lg transition-all ${
                        message.type === 'input'
                          ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-600'
                          : 'text-blue-100 hover:text-white hover:bg-blue-700'
                      } ${playingMessageId === `${message.id}-slow` ? 'animate-pulse' : ''}`}
                      onClick={() => handlePlayMessage(message.id, message.text, message.language, 'slow')}
                    >
                      <SnailIcon size={24} />
                    </button>
                    <button
                      className={`p-2 rounded-lg transition-all ${
                        message.type === 'input'
                          ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-600'
                          : 'text-blue-100 hover:text-white hover:bg-blue-700'
                      } ${playingMessageId === `${message.id}-normal` ? 'animate-pulse' : ''}`}
                      onClick={() => handlePlayMessage(message.id, message.text, message.language, 'normal')}
                    >
                      <Volume2 size={24} />
                    </button>
                    <button
                      className={`p-2 rounded-lg transition-all ${
                        message.type === 'input'
                          ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-600'
                          : 'text-blue-100 hover:text-white hover:bg-blue-700'
                      }`}
                      onClick={() => handleCopyMessage(message.text)}
                    >
                      <Copy size={24} />
                    </button>
                  </div>
                </div>
                {/* Message text below buttons */}
                <div className="px-5 pb-4">
                  <p className="text-lg leading-relaxed">
                    {message.text}
                  </p>
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {savedConversations.length === 0 ? (
              <div className="text-center text-gray-400 mt-10">
                <p>No saved conversations yet</p>
              </div>
            ) : (
              <div className="space-y-6">
                {savedConversations.map((conversation, index) => (
                  <div key={index} className="text-white">
                    {/* Line 1: Date/Time, Languages, and Trash icon */}
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-white">
                        {formatDateTime(conversation.timestamp)} {availableLanguages[conversation.originalLanguage]} {availableLanguages[conversation.translation1Language]} {availableLanguages[conversation.translation2Language]}
                      </span>
                      <button
                        className="text-gray-400 hover:text-gray-200"
                        onClick={() => deleteConversation(index)}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                    {/* Line 2: Original text with Chevron and Eye icon */}
                    <div className="flex justify-between items-start mb-1">
                      <span
                        className="text-sm cursor-pointer flex-1"
                        onClick={() => toggleLineExpansion(index, 'original')}
                      >
                        {expandedLines[`${index}-original`]
                          ? conversation.originalText
                          : truncateText(conversation.originalText)}
                      </span>
                      <div className="flex items-start ml-2 space-x-1">
                        {conversation.originalText.length > 50 && (
                          <button
                            className="text-gray-400 hover:text-gray-200"
                            onClick={() => toggleLineExpansion(index, 'original')}
                          >
                            {expandedLines[`${index}-original`] ? (
                              <ChevronDown size={18} />
                            ) : (
                              <ChevronRight size={18} />
                            )}
                          </button>
                        )}
                        <button
                          className="text-gray-400 hover:text-gray-200"
                          onClick={() => loadConversationToView(conversation, 'input')}
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                    </div>
                    {/* Line 3: Translation 1 with Chevron and Eye icon */}
                    <div className="flex justify-between items-start mb-1">
                      <span
                        className="text-sm cursor-pointer flex-1"
                        onClick={() => toggleLineExpansion(index, 'translation1')}
                      >
                        {expandedLines[`${index}-translation1`]
                          ? conversation.translation1Text
                          : truncateText(conversation.translation1Text)}
                      </span>
                      <div className="flex items-start ml-2 space-x-1">
                        {conversation.translation1Text.length > 50 && (
                          <button
                            className="text-gray-400 hover:text-gray-200"
                            onClick={() => toggleLineExpansion(index, 'translation1')}
                          >
                            {expandedLines[`${index}-translation1`] ? (
                              <ChevronDown size={18} />
                            ) : (
                              <ChevronRight size={18} />
                            )}
                          </button>
                        )}
                        <button
                          className="text-gray-400 hover:text-gray-200"
                          onClick={() => loadConversationToView(conversation, 'translation1')}
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                    </div>
                    {/* Line 4: Translation 2 with Chevron and Eye icon */}
                    <div className="flex justify-between items-start">
                      <span
                        className="text-sm cursor-pointer flex-1"
                        onClick={() => toggleLineExpansion(index, 'translation2')}
                      >
                        {expandedLines[`${index}-translation2`]
                          ? conversation.translation2Text
                          : truncateText(conversation.translation2Text)}
                      </span>
                      <div className="flex items-start ml-2 space-x-1">
                        {conversation.translation2Text.length > 50 && (
                          <button
                            className="text-gray-400 hover:text-gray-200"
                            onClick={() => toggleLineExpansion(index, 'translation2')}
                          >
                            {expandedLines[`${index}-translation2`] ? (
                              <ChevronDown size={18} />
                            ) : (
                              <ChevronRight size={18} />
                            )}
                          </button>
                        )}
                        <button
                          className="text-gray-400 hover:text-gray-200"
                          onClick={() => loadConversationToView(conversation, 'translation2')}
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Input Lines */}
        {currentView === 'translate' && (
        <div className="p-4 space-y-4">
          {selectedLanguages.map((language, index) => (
            <div key={index} className={`flex items-center space-x-2 px-3 py-3 rounded-full transition-all ${
              activeLanguage === language ? 'bg-blue-600 text-white' : 'text-gray-300'
            }`}>
              <button
                onClick={() => handleImageUpload(language)}
                className={`transition-colors ${
                  activeLanguage === language ? 'text-white hover:text-blue-200' : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>

              <select
                value={language}
                onChange={(e) => handleLanguageChange(index, e.target.value)}
                className={`text-sm py-0 px-0 bg-transparent border-0 focus:outline-none appearance-none ${
                  activeLanguage === language ? 'text-white' : 'text-gray-300'
                }`}
              >
                {Object.entries(availableLanguages).map(([code, name]) => {
                  const isSelected = selectedLanguages.includes(code);
                  const isCurrentDropdown = selectedLanguages[index] === code;
                  
                  return (
                    <option 
                      key={code} 
                      value={code}
                      disabled={isSelected && !isCurrentDropdown}
                      style={{ 
                        color: isSelected && !isCurrentDropdown ? '#999' : '#000',
                        backgroundColor: isSelected && !isCurrentDropdown ? '#f5f5f5' : 'white'
                      }}
                    >
                      {name}
                    </option>
                  );
                })}
              </select>

              <input
                type="text"
                value={inputs[language] || ''}
                onChange={(e) => setInputs(prev => ({ ...prev, [language]: e.target.value }))}
                onFocus={() => setActiveLanguage(language)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(language)}
                placeholder={getPlaceholderText(language)}
                className={`flex-1 py-0 px-1 text-sm bg-transparent border-0 focus:outline-none ${
                  activeLanguage === language ? 'text-white placeholder-blue-200 caret-white' : 'text-gray-300 placeholder-gray-500 caret-transparent'
                }`}
              />

              <button
                onClick={() => startRecording(language)}
                className={`p-2 rounded-full transition-all ${
                  isRecording === language 
                    ? 'bg-white bg-opacity-20 shadow-lg' 
                    : ''
                }`}
              >
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke={activeLanguage === language ? '#ffffff' : '#9ca3af'}
                  strokeWidth="2"
                  className={isRecording === language ? 'pulse-mic' : ''}
                >
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="23"/>
                  <line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
              </button>

              <button
                onClick={() => handleSendMessage(language)}
                disabled={!inputs[language]?.trim()}
                className={`p-2 transition-colors ${
                  inputs[language]?.trim()
                    ? (activeLanguage === language ? 'text-white hover:text-blue-200' : 'text-blue-400 hover:text-blue-300')
                    : (activeLanguage === language ? 'text-blue-200' : 'text-gray-500')
                }`}
              >
                <Send size={22} />
              </button>
            </div>
          ))}
        </div>
        )}
      </div>
    </>
  );
};

export default TrilingualTranslator;